// src/api/supabaseClient.js
//
// Cliente Supabase centralizado.
// - Singleton (uma instância pra app inteiro)
// - Storage com fallback (Edge Tracking Prevention bloqueia localStorage em alguns browsers)
// - Helpers dbInsert/dbUpdate que validam tenant antes da chamada
// - Defesa: bloqueia INSERT em fluxo_caixa direto (use RPC marcar_conta_paga)

import { createClient } from '@supabase/supabase-js';
import { tenant } from '../context/tenantContext.js';

const URL = import.meta.env.VITE_SUPABASE_URL;
const KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!URL || !KEY) {
  throw new Error(
    '[supabaseClient] VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são obrigatórios. ' +
    'Copie app/.env.staging.example para app/.env.staging.local e preencha.'
  );
}

// Fallback storage · sessionStorage quando localStorage está bloqueado
const _authStorage = (() => {
  try {
    localStorage.setItem('_sbtest', '1');
    localStorage.removeItem('_sbtest');
    return localStorage;
  } catch {
    return sessionStorage;
  }
})();

export const sb = createClient(URL, KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Recovery determinístico: o token da URL NÃO é consumido automaticamente —
    // o boot (main.js) instala a sessão de recovery explicitamente, sobrescrevendo
    // qualquer sessão pré-existente. Sem isto, reset de senha num navegador com
    // outro usuário logado alterava a senha do usuário ERRADO.
    detectSessionInUrl: false,
    storage: _authStorage,
    storageKey: 'sb-angular-erp-auth',
  },
  global: {
    headers: {
      'X-Client-Info': `angular-erp-vite/${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,
    },
  },
});

// ── Helpers tenant-aware ─────────────────────────────────────────────
// Lançam erro se não há tenant + não é super_admin.
// Garantia: nenhum INSERT vai pro banco sem empresa_id explícito (RLS bloquearia mas falha tarde).

const TABELAS_BLOQUEADAS_INSERT_DIRETO = new Set([
  'fluxo_caixa',  // só via RPC marcar_conta_paga (regra CLAUDE.md fix dual-write)
]);

/**
 * Insert tenant-aware.
 * Recusa tabelas bloqueadas (devem usar RPC).
 * Auto-popula empresa_id se não vier no payload.
 */
export function dbInsert(table, payload, options = {}) {
  if (TABELAS_BLOQUEADAS_INSERT_DIRETO.has(table)) {
    throw new Error(
      `[supabaseClient] INSERT direto em "${table}" proibido. ` +
      `Use a RPC adequada (ex: sb.rpc('marcar_conta_paga', {...})).`
    );
  }
  if (!tenant.empresaId && !tenant.isSuperAdmin) {
    throw new Error(`[supabaseClient] dbInsert("${table}") chamado sem tenant ativo.`);
  }

  const arr = Array.isArray(payload) ? payload : [payload];
  const withTenant = arr.map((row) => ({
    empresa_id: tenant.empresaId, // sobrescrito pelo row caso esteja explícito
    ...row,
  }));
  return sb.from(table).insert(Array.isArray(payload) ? withTenant : withTenant[0], options);
}

/**
 * RPC tenant-aware · só verifica que há tenant.
 * (O empresa_id é resolvido server-side via current_empresa_id() na RPC SECURITY DEFINER)
 */
export async function rpcCall(fnName, args = {}) {
  if (!tenant.isHydrated) throw new Error(`[supabaseClient] rpcCall("${fnName}") sem tenant hidratado.`);
  return sb.rpc(fnName, args);
}

// ── debug · só em dev/staging ────────────────────────────────────────
if (import.meta.env.MODE !== 'production') {
  // Não exporta sb em window — quem quiser inspecionar precisa importar o módulo.
  // Mas para conveniência DEV, expomos um snapshot read-only.
  // Removido em build de produção via tree-shaking (NODE_ENV !== prod).
}
